import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import { BiBell } from 'react-icons/bi';
import '../style.css';

const NotificationIcon = () => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/notifications');
      const notificationsList = (response.data || []).map(n => ({
        ...n, read: n.read ? 1 : 0
      }));

      // ✅ DEDUPLICATE NOTIFICATIONS BY ID
      const uniqueNotifications = [];
      const seenIds = new Set();
      
      for (const notification of notificationsList) {
        if (!seenIds.has(notification.id)) {
          seenIds.add(notification.id);
          uniqueNotifications.push(notification);
        }
      }

      setNotifications(uniqueNotifications);
      setUnreadCount(uniqueNotifications.filter(n => n.read === 0).length);
    } catch (error) {
      setNotifications([]);
      setUnreadCount(0);
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: 1 } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: 1 })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      fetchNotifications();
    }
  };

  const getTimeString = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return notificationTime.toLocaleDateString();
  };

  return (
    <div className="notification-wrapper" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="notification-button"
        aria-label="Notifications"
      >
        <BiBell size={24} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>
      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read"
                onClick={markAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <p className="notification-empty">No notifications</p>
          ) : (
            <div className="notification-list">
              {notifications.map((notification, index) => (
                <div
                  key={`notification-${notification.id}-${index}`}
                  className={`notification-item ${notification.read === 0 ? 'unread' : ''}`}
                  onClick={() =>
                    notification.read === 0 && markAsRead(notification.id)
                  }
                >
                  <div className="notification-content">
                    <p className="notification-title">{notification.title || 'Notification'}</p>
                    <p className="notification-message">{notification.message}</p>
                    <p className="notification-time">
                      {getTimeString(notification.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default NotificationIcon;
